<template>
  <div>
    <template v-if="showEdit">
      <div class="row mb-4 pl-3">
        <div class="col-sm">
          <validation-provider
            v-slot="{ errors }"
            name="Teléfono"
            rules="required|length:11"
          >
            <custom-input
              v-model="model.locationTelephoneNo"
              custom-error-msg="Campo Requerido"
              :error="errors[0]"
              label="Teléfono:"
              mask-type="Phone"
              placeholder="(___) ___-____"
            />
          </validation-provider>
        </div>

        <div class="col-sm">
          <custom-input
            v-model="model.locationTelephoneFaxNo"
            label="Fax:"
            mask-type="Phone"
            placeholder="(___) ___-____"
          />
        </div>

        <div class="col-sm">
          <custom-input
            v-model="model.locationWebsiteUrl"
            label="Dirección Web:"
            placeholder="www.websitedomain.com"
          />
        </div>
        <div class="col-sm d-flex align-items-end">
          <slot />
        </div>
      </div>
    </template>

    <template v-else>
      <div class="row mb-4 pl-3">
        <div class="col-4 my-2">
          <custom-input-display
            v-model="model.locationTelephoneNo"
            label="Teléfono:"
            :mask-type="'Phone'"
            placeholder="(___) ___-____"
          />
        </div>

        <div class="col-4 my-2">
          <custom-input-display
            v-model="model.locationTelephoneFaxNo"
            label="Fax:"
            :mask-type="'Phone'"
            placeholder="(___) ___-____"
          />
        </div>

        <div class="col-4 my-2">
          <custom-input-display
            v-model="model.locationWebsiteUrl"
            label="Dirección Web:"
          />
        </div>
      </div>
      <div v-if="isMedicaidDocumentationWorkflowEnabled" class="row my-2">
        <div class="col-sm-3 my-2">
          <custom-input-display
            v-if="showSchoolSection"
            label="NPI LS DEPR:"
            :value="model.locationSchoolNationalIdentifier"
          />

          <custom-input
            v-else
            v-model="model.locationSchoolNationalIdentifier"
            :disabled="isNpiDisabled"
            label="NPI LS DEPR:"
            placeholder="Ingrese el NPI LS DEPR"
          />
        </div>

        <div class="col-sm-3 my-2">
          <custom-input
            v-model="model.nationalIdentifier"
            :disabled="isNpiDisabled"
            label="NPI LS Proveedor:"
            placeholder="Ingrese el NPI LS Proveedor"
          />
        </div>

        <div class="col-sm-2 my-2">
          <custom-input
            v-model="model.medicalIdentifier"
            :disabled="isNpiDisabled"
            label="Medicaid ID:"
            maxlength="50"
            placeholder="Ingrese el Medicaid ID"
          />
        </div>
        <div class="row col-sm-4 my-2">
          <div class="col-sm">
            <custom-date-picker
              v-model="model.medicaidDueDate"
              class-container="w-100"
              :disabled="isNpiDisabled"
              format="dd/MMM./yyyy"
              label="Fecha de Vencimiento Medicaid:"
              :min-date="currentDate"
              plcaeholder="Seleccione la fecha de vencimiento del Medicaid"
            />
          </div>
          <div class="d-flex" :class="{ 'ml-3': !isNpiDisabled }">
            <div class="mt-4">
              <div v-if="editingNpi">
                <button
                  v-if="canCancelNpi && initialNpi"
                  class="ab-btn ab-btn-outline-danger mt-1 mr-2"
                  type="button"
                  variant="outline-danger mt-1 mr-2"
                  @click="cancelEditNpi"
                >
                  <i class="fas fa-times-circle" />
                </button>

                <button
                  class="ab-btn ab-btn-success mt-1"
                  type="button"
                  @click="saveNpi"
                >
                  <i class="fas fa-save " />
                </button>
              </div>
              <button
                v-else
                class="ab-btn ab-btn-success mt-1"
                type="button"
                @click="editNpi"
              >
                <i class="fas fa-edit " />
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { ValidationProvider } from 'vee-validate';
import CustomInput from '@/components/common/form/CustomInput';
import CustomInputDisplay from '@/components/common/form/CustomInputDisplay';
import CustomDatePicker from '@/components/common/form/CustomDatePicker';

export default {
  name: 'GeneralInformation',
  components: {
    CustomInput,
    ValidationProvider,
    CustomInputDisplay,
    CustomDatePicker,
  },
  props: {
    showSchoolSection: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Object,
      default: () => ({
        locationTelephoneNo: '',
        locationTelephoneFaxNo: '',
        locationWebsiteUrl: '',
      }),
    },
    showEdit: {
      type: Boolean,
      default: false,
    },
    initialNpi: {
      type: String,
      default: '',
    },
    initialNpiDepr: {
      type: String,
      default: '',
    },
    isMedicaidDocumentationWorkflowEnabled: {
      type: Boolean,
      default: false,
    },
    initialMedicalIdentifier: {
      type: String,
      default: '',
    },
    initialMedicaidDueDate: {
      type: Date,
      default: '',
    },
  },
  data() {
    return {
      editingNpi: false,
      canCancelNpi: false,
      editingNpiDepr: false,
      canCancelNpiDepr: false,
      model: {
        locationTelephoneNo: '',
        locationTelephoneFaxNo: '',
        locationWebsiteUrl: '',
        nationalIdentifier: '',
        locationSchoolNationalIdentifier: '',
        medicalIdentifier: null,
        medicaidDueDate: null,
      },
    };
  },
  computed: {
    currentDate() {
      return new Date();
    },
    isNpiDisabled() {
      return !this.editingNpi && !!this.initialNpi;
    },
  },
  watch: {
    value: {
      deep: true,
      immediate: true,
      handler(newVal) {
        if (!newVal) return;

        const updated = {
          locationTelephoneNo: newVal.locationTelephoneNo || '',
          locationTelephoneFaxNo: newVal.locationTelephoneFaxNo || '',
          locationWebsiteUrl: newVal.locationWebsiteUrl || '',
          nationalIdentifier: newVal.nationalIdentifier || '',
          locationSchoolNationalIdentifier:
            newVal.locationSchoolNationalIdentifier || '',
          medicaidDueDate: newVal.medicaidDueDate || null,
          medicalIdentifier: newVal.medicalIdentifier || null,
        };

        if (JSON.stringify(updated) !== JSON.stringify(this.model)) {
          this.model = { ...updated };
        }
      },
    },
    model: {
      deep: true,
      handler(newVal) {
        if (!newVal) return;

        const comparable = {
          locationTelephoneNo: this.value.locationTelephoneNo || '',
          locationTelephoneFaxNo: this.value.locationTelephoneFaxNo || '',
          locationWebsiteUrl: this.value.locationWebsiteUrl || '',
          nationalIdentifier: this.value.nationalIdentifier || '',
          locationSchoolNationalIdentifier:
            this.value.locationSchoolNationalIdentifier || '',
          medicaidDueDate: this.value.medicaidDueDate || null,
          medicalIdentifier: this.value.medicalIdentifier || null,
        };

        if (JSON.stringify(newVal) !== JSON.stringify(comparable)) {
          this.$emit('input', { ...this.value, ...newVal });
        }
      },
    },
    initialNpi: {
      immediate: true,
      handler(val) {
        this.editingNpi = !val;
      },
    },
    initialNpiDepr: {
      immediate: true,
      handler(val) {
        this.editingNpiDepr = !val;
      },
    },
  },
  methods: {
    editNpi() {
      this.editingNpi = true;
      this.canCancelNpi = true;
    },
    cancelEditNpi() {
      this.model.nationalIdentifier = this.initialNpi || '';
      this.model.locationSchoolNationalIdentifier = this.initialNpiDepr || '';
      this.model.medicalIdentifier = this.initialMedicalIdentifier;
      this.model.medicaidDueDate = this.initialMedicaidDueDate;

      this.editingNpi = false;
    },
    async saveNpi() {
      this.$emit('save-npi', this.model, () => {
        this.editingNpi = false;
      });
    },
  },
};
</script>
<style scoped>
::v-deep .input-display > label:first-child {
  font-size: 14px !important;
  color: #727272 !important;
}
</style>
